import { useCallback, useRef, useState } from "react";

import type { IMultiAnalysisTimeRange } from "../types";
import {
  clampFrameRange,
  createRangeFromTimestamps,
  moveFrameRange,
  normalizeTimeRange,
  ratioToTimestamp,
} from "../utils/range-frame";

export type TRangeFrameDragMode = "move" | "resize-left" | "resize-right";

interface IUseRangeFrameDragArgs {
  globalRange: IMultiAnalysisTimeRange;
  frameRange: IMultiAnalysisTimeRange;
  onFrameRangeChange: (range: IMultiAnalysisTimeRange) => void;
  plotLeft: number;
  plotWidth: number;
}

export const useRangeFrameDrag = ({
  globalRange,
  frameRange,
  onFrameRangeChange,
  plotLeft,
  plotWidth,
}: IUseRangeFrameDragArgs) => {
  const dragModeRef = useRef<TRangeFrameDragMode | null>(null);
  const frameSnapshotRef = useRef<IMultiAnalysisTimeRange>(frameRange);
  const pointerStartTimestampRef = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  const pointerToTimestamp = useCallback(
    (clientX: number, containerLeft: number): number => {
      const ratio = plotWidth > 0 ? (clientX - containerLeft - plotLeft) / plotWidth : 0;

      return ratioToTimestamp(ratio, globalRange);
    },
    [globalRange, plotLeft, plotWidth],
  );

  const handlePointerDown = useCallback(
    (mode: TRangeFrameDragMode, event: React.PointerEvent, containerLeft: number) => {
      event.preventDefault();
      event.stopPropagation();
      dragModeRef.current = mode;
      frameSnapshotRef.current = frameRange;
      pointerStartTimestampRef.current = pointerToTimestamp(event.clientX, containerLeft);
      setIsDragging(true);
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    },
    [frameRange, pointerToTimestamp],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent, containerLeft: number) => {
      const mode = dragModeRef.current;

      if (!mode) {
        return;
      }

      const timestamp = pointerToTimestamp(event.clientX, containerLeft);
      const snapshot = normalizeTimeRange(frameSnapshotRef.current);
      const globalBounds = normalizeTimeRange(globalRange);

      if (!snapshot || !globalBounds) {
        return;
      }

      if (mode === "move") {
        const delta = timestamp - pointerStartTimestampRef.current;
        onFrameRangeChange(clampFrameRange(moveFrameRange(frameSnapshotRef.current, globalRange, delta), globalRange));

        return;
      }

      if (mode === "resize-left") {
        onFrameRangeChange(
          clampFrameRange(createRangeFromTimestamps(timestamp, snapshot.end, globalRange.start), globalRange),
        );

        return;
      }

      onFrameRangeChange(
        clampFrameRange(createRangeFromTimestamps(snapshot.start, timestamp, globalRange.start), globalRange),
      );
    },
    [globalRange, onFrameRangeChange, pointerToTimestamp],
  );

  const handlePointerUp = useCallback((event: React.PointerEvent) => {
    dragModeRef.current = null;
    setIsDragging(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    isDragging,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
};
